import React from 'react';
import * as d3 from 'd3';
import { Theme } from '../../../theme';

export interface TweetData {
  id: string;
  predictionId?: number;
  text: string;
  expert_name: string;
  twitter_handle: string;
  verified: boolean;
  profile_image_url?: string;
  source_url?: string;
  prediction_date?: string;
  isBetPrediction?: boolean;
  isWinner?: boolean;
  isLoser?: boolean;
}

/**
 * BASE CONFIGURATION - All values defined once, scaled uniformly
 * No more special cases per expert - the layout adapts dynamically
 */
const BASE = {
  padding: 12,
  avatarSize: 44,  // Increased from 36
  contentGap: 10,
  nameFontSize: 14,
  handleFontSize: 11,
  textFontSize: 13,
  lineHeight: 18,
  buttonSize: 20,
  buttonGap: 24,
  cornerRadius: 8,
  headerHeight: 48,  // Increased to fit larger avatar
  footerHeight: 32,
  minCardHeight: 90,
  charWidthRatio: 0.58, // Character width as ratio of font size
};

/**
 * Create tweet card visualization with UNIFIED SCALING
 * 
 * Key principle: ALL dimensions scale together proportionally.
 * When scaleFactor = 1.5, everything is 50% bigger including card width.
 */
export const createTweetVisualization = (
  parent: d3.Selection<SVGGElement, unknown, null, undefined>,
  tweetData: TweetData,
  x: number,
  y: number,
  theme: Theme,
  baseWidth: number = 280,
  fixedHeight?: number,
  scaleFactor: number = 1.0
) => {
  // ========== DETECT SCREENSHOT MODE ==========
  const isScreenshotMode = typeof window !== 'undefined' && 
    new URLSearchParams(window.location.search).get('screenshot') === 'true';
  
  // Screenshot boost: 40% larger for social media visibility
  const screenshotBoost = isScreenshotMode ? 1.4 : 1.0;
  const scale = scaleFactor * screenshotBoost;
  
  // ========== UNIFORMLY SCALED DIMENSIONS ==========
  // Everything scales together - card width, fonts, padding, all of it
  const cardWidth = baseWidth * scale;
  const padding = BASE.padding * scale;
  const avatarSize = BASE.avatarSize * scale;
  const contentGap = BASE.contentGap * scale;
  const nameFontSize = BASE.nameFontSize * scale;
  const handleFontSize = BASE.handleFontSize * scale;
  const textFontSize = BASE.textFontSize * scale;
  const lineHeight = BASE.lineHeight * scale;
  const buttonSize = BASE.buttonSize * scale;
  const buttonGap = BASE.buttonGap * scale;
  const cornerRadius = BASE.cornerRadius * scale;
  const headerHeight = BASE.headerHeight * scale;
  const footerHeight = BASE.footerHeight * scale;

  // ========== TEXT PROCESSING ==========
  // Truncation limit based on mode
  const maxChars = isScreenshotMode ? 2000 : 350;
  let displayText = tweetData.text || '';
  const wasCharTruncated = displayText.length > maxChars;
  
  if (displayText.length > maxChars) {
    const cut = displayText.substring(0, maxChars);
    const lastSpace = cut.lastIndexOf(' ');
    displayText = (lastSpace > maxChars * 0.7 ? cut.substring(0, lastSpace) : cut) + '...';
  }

  // ========== DYNAMIC LINE CALCULATION ==========
  // Text area = card width minus padding, avatar, gap
  const textAreaWidth = cardWidth - padding * 2 - avatarSize - contentGap;
  
  // Character width estimation (proportional to font size)
  const avgCharWidth = textFontSize * BASE.charWidthRatio;
  const charsPerLine = Math.floor(textAreaWidth / avgCharWidth);
  
  // Word wrap simulation
  const words = displayText.split(' ');
  let lineCount = 1;
  let lineLength = 0;
  
  for (const word of words) {
    const wordLen = word.length + 1;
    if (lineLength + wordLen > charsPerLine && lineLength > 0) {
      lineCount++;
      lineLength = word.length;
    } else {
      lineLength += wordLen;
    }
  }

  // ========== CARD HEIGHT CALCULATION ==========
  const textHeight = lineCount * lineHeight;
  const calculatedHeight = padding + headerHeight + textHeight + footerHeight;
  const cardHeight = fixedHeight || Math.max(calculatedHeight, BASE.minCardHeight * scale);
  // ========== CREATE SVG GROUP ==========
  const cardGroup = parent.append('g')
    .attr('class', 'tweet-card')
    .style('cursor', 'default');

  // ========== CARD BACKGROUND ==========
  cardGroup.append('rect')
    .attr('class', 'tweet-card-bg')
    .attr('x', x)
    .attr('y', y)
    .attr('width', cardWidth)
    .attr('height', cardHeight)
    .attr('rx', cornerRadius)
    .attr('ry', cornerRadius)
    .attr('fill', theme.tweet.backgroundColor)
    .attr('stroke', theme.tweet.borderColor)
    .attr('stroke-width', 1)
    .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.12))');

  // ========== CLOSE BUTTON (top-right corner) ==========
  // Skip in screenshot mode for cleaner captures
  if (!isScreenshotMode && tweetData.predictionId) {
    const closeBtnSize = 18 * scale;
    const closeBtnX = x + cardWidth - closeBtnSize / 2 - padding * 0.6;
    const closeBtnY = y + closeBtnSize / 2 + padding * 0.6;
    const isLight = theme.tweet.backgroundColor === '#ffffff';
    
    const closeGroup = cardGroup.append('g')
      .attr('class', 'close-button')
      .style('cursor', 'pointer')
      .style('opacity', 0.5)
      .on('mouseover', function() { d3.select(this).style('opacity', 1); })
      .on('mouseout', function() { d3.select(this).style('opacity', 0.5); })
      .on('click', (e) => {
        e.stopPropagation();
        // Dispatch custom event to toggle this prediction off
        const event = new CustomEvent('prediction-close', { 
          detail: { predictionId: tweetData.predictionId },
          bubbles: true 
        });
        window.dispatchEvent(event);
      });

    // Close button background circle
    closeGroup.append('circle')
      .attr('cx', closeBtnX)
      .attr('cy', closeBtnY)
      .attr('r', closeBtnSize / 2)
      .attr('fill', isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)')
      .attr('stroke', 'none');

    // X icon
    const xSize = closeBtnSize * 0.28;
    const xColor = isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)';
    closeGroup.append('path')
      .attr('d', `M ${closeBtnX - xSize} ${closeBtnY - xSize} L ${closeBtnX + xSize} ${closeBtnY + xSize}
                  M ${closeBtnX + xSize} ${closeBtnY - xSize} L ${closeBtnX - xSize} ${closeBtnY + xSize}`)
      .attr('stroke', xColor)
      .attr('stroke-width', 2 * scale)
      .attr('stroke-linecap', 'round')
      .attr('fill', 'none');
  }

  // ========== AVATAR ==========
  const avatarX = x + padding;
  const avatarY = y + padding;
  
  // Background circle
  cardGroup.append('circle')
    .attr('cx', avatarX + avatarSize / 2)
    .attr('cy', avatarY + avatarSize / 2)
    .attr('r', avatarSize / 2)
    .attr('fill', '#e1e8ed');

  // Profile image or placeholder
  if (tweetData.profile_image_url) {
    cardGroup.append('image')
      .attr('x', avatarX)
      .attr('y', avatarY)
      .attr('width', avatarSize)
      .attr('height', avatarSize)
      .attr('href', tweetData.profile_image_url)
      .attr('clip-path', `circle(${avatarSize/2}px at ${avatarSize/2}px ${avatarSize/2}px)`);
  } else {
    cardGroup.append('text')
      .attr('x', avatarX + avatarSize / 2)
      .attr('y', avatarY + avatarSize / 2 + avatarSize * 0.12)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .attr('font-size', `${avatarSize * 0.45}px`)
      .attr('font-weight', 'bold')
      .attr('fill', '#657786')
      .text((tweetData.expert_name || '?').charAt(0).toUpperCase());
  }

  // ========== NAME & HANDLE ==========
  const textStartX = avatarX + avatarSize + contentGap;
  
  // Expert name
  cardGroup.append('text')
    .attr('x', textStartX)
    .attr('y', y + padding + nameFontSize)
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .attr('font-size', `${nameFontSize}px`)
    .attr('font-weight', 'bold')
    .attr('fill', theme.tweet.nameColor)
    .text(tweetData.expert_name || 'Unknown');

  // Handle + optional date
  const handle = tweetData.twitter_handle?.startsWith('@') 
    ? tweetData.twitter_handle 
    : `@${tweetData.twitter_handle || 'unknown'}`;
  
  let handleLine = handle;
  if (tweetData.prediction_date) {
    try {
      const date = new Date(tweetData.prediction_date);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      handleLine = `${handle} · ${dateStr}`;
    } catch (e) {
      // Keep just handle if date fails
    }
  }
  
  cardGroup.append('text')
    .attr('x', textStartX)
    .attr('y', y + padding + nameFontSize + handleFontSize + 3 * scale)
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .attr('font-size', `${handleFontSize}px`)
    .attr('fill', theme.tweet.handleColor)
    .text(handleLine);

  // ========== TWEET TEXT (word-wrapped) ==========
  const textY = y + padding + headerHeight;
  let currentLine = '';
  let currentWidth = 0;
  let yOffset = 0;
  
  for (const word of words) {
    const wordWidth = (word.length + 1) * avgCharWidth;
    
    if (currentWidth + wordWidth > textAreaWidth && currentWidth > 0) {
      // Render line
      cardGroup.append('text')
        .attr('x', textStartX)
        .attr('y', textY + yOffset)
        .attr('font-family', 'system-ui, -apple-system, sans-serif')
        .attr('font-size', `${textFontSize}px`)
        .attr('fill', theme.tweet.textColor)
        .text(currentLine.trim());
      
      currentLine = word;
      currentWidth = word.length * avgCharWidth;
      yOffset += lineHeight;
    } else {
      currentLine += (currentLine ? ' ' : '') + word;
      currentWidth += wordWidth;
    }
  }
  
  // Final line
  if (currentLine) {
    const finalLine = wasCharTruncated ? `${currentLine.trim()}...` : currentLine.trim();
    cardGroup.append('text')
      .attr('x', textStartX)
      .attr('y', textY + yOffset)
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .attr('font-size', `${textFontSize}px`)
      .attr('fill', theme.tweet.textColor)
      .text(finalLine);
  }

  // ========== BUTTONS (bottom right) ==========
  const buttonY = y + cardHeight - padding - buttonSize / 2;
  let buttonX = x + cardWidth - padding - buttonSize / 2;
  const btnRadius = buttonSize / 2;

  // Source link button
  if (tweetData.source_url) {
    const srcGroup = cardGroup.append('g')
      .attr('class', 'source-button')
      .style('cursor', 'pointer')
      .on('click', (e) => { e.stopPropagation(); window.open(tweetData.source_url!, '_blank'); });

    srcGroup.append('circle')
      .attr('cx', buttonX)
      .attr('cy', buttonY)
      .attr('r', btnRadius)
      .attr('fill', '#1a1a1a')
      .attr('stroke', '#F7931A')
      .attr('stroke-width', 1.5 * scale);

    // Arrow icon
    const arr = btnRadius * 0.45;
    srcGroup.append('path')
      .attr('d', `M ${buttonX - arr} ${buttonY} L ${buttonX + arr} ${buttonY}
                  M ${buttonX + arr * 0.3} ${buttonY - arr * 0.6} L ${buttonX + arr} ${buttonY} L ${buttonX + arr * 0.3} ${buttonY + arr * 0.6}`)
      .attr('stroke', '#F7931A')
      .attr('stroke-width', 1.5 * scale)
      .attr('fill', 'none')
      .attr('stroke-linecap', 'round');

    buttonX -= buttonGap;
  }

  // Share button (copy link)
  if (tweetData.predictionId) {
    const shareUrl = `https://proofofprice.com/btc?p=${tweetData.predictionId}`;
    const isLight = theme.tweet.backgroundColor === '#ffffff';
    
    const shareGroup = cardGroup.append('g')
      .attr('class', 'share-button')
      .style('cursor', 'pointer')
      .on('click', function(e: any) {
        e.stopPropagation();
        e.preventDefault();
        
        const circle = d3.select(this).select('circle');
        const icon = d3.select(this).selectAll('path, line');
        
        // Immediate visual feedback
        circle.attr('fill', '#4CAF50'); // Green for success
        icon.attr('stroke', '#fff');
        
        // Copy to clipboard
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(shareUrl).then(() => {
            // Show checkmark briefly then reset
            setTimeout(() => {
              circle.attr('fill', isLight ? '#f0f0f0' : '#1a1a1a');
              icon.attr('stroke', isLight ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)');
            }, 800);
          }).catch(() => {
            // Fallback: open in new tab
            window.open(shareUrl, '_blank');
            circle.attr('fill', isLight ? '#f0f0f0' : '#1a1a1a');
            icon.attr('stroke', isLight ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)');
          });
        } else {
          // Fallback for older browsers
          window.open(shareUrl, '_blank');
          setTimeout(() => {
            circle.attr('fill', isLight ? '#f0f0f0' : '#1a1a1a');
            icon.attr('stroke', isLight ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)');
          }, 300);
        }
      });

    shareGroup.append('circle')
      .attr('cx', buttonX)
      .attr('cy', buttonY)
      .attr('r', btnRadius)
      .attr('fill', isLight ? '#f0f0f0' : '#1a1a1a')
      .attr('stroke', isLight ? 'rgba(0,0,0,0.15)' : 'rgba(128,128,128,0.4)')
      .attr('stroke-width', 1.5 * scale);

    // Link/chain icon (cleaner design)
    const iconCol = isLight ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)';
    const s = btnRadius * 0.35; // icon size
    
    // Two overlapping chain links
    shareGroup.append('path')
      .attr('d', `M ${buttonX - s * 0.3} ${buttonY - s * 0.5} 
                  L ${buttonX + s * 0.7} ${buttonY - s * 0.5}
                  A ${s * 0.5} ${s * 0.5} 0 0 1 ${buttonX + s * 0.7} ${buttonY + s * 0.1}
                  L ${buttonX + s * 0.1} ${buttonY + s * 0.1}`)
      .attr('stroke', iconCol)
      .attr('stroke-width', 1.8 * scale)
      .attr('fill', 'none')
      .attr('stroke-linecap', 'round');
    
    shareGroup.append('path')
      .attr('d', `M ${buttonX + s * 0.3} ${buttonY + s * 0.5} 
                  L ${buttonX - s * 0.7} ${buttonY + s * 0.5}
                  A ${s * 0.5} ${s * 0.5} 0 0 1 ${buttonX - s * 0.7} ${buttonY - s * 0.1}
                  L ${buttonX - s * 0.1} ${buttonY - s * 0.1}`)
      .attr('stroke', iconCol)
      .attr('stroke-width', 1.8 * scale)
      .attr('fill', 'none')
      .attr('stroke-linecap', 'round');
  }

  return { cardGroup, cardHeight };
};

// ========== REACT COMPONENT (simplified version) ==========
export interface TweetVisualizationProps {
  tweetData: TweetData;
  x: number;
  y: number;
  maxWidth?: number;
  onClick?: () => void;
  theme?: 'light' | 'dark';
}

const TweetVisualization: React.FC<TweetVisualizationProps> = ({ 
  tweetData, x, y, maxWidth = 280, onClick, theme = 'light'
}) => {
  const textAreaWidth = maxWidth - BASE.padding * 2 - BASE.avatarSize - BASE.contentGap;
  const avgCharWidth = BASE.textFontSize * BASE.charWidthRatio;
  const charsPerLine = Math.floor(textAreaWidth / avgCharWidth);
  
  const displayText = tweetData.text || '';
  const words = displayText.split(' ');
  
  // Calculate lines
  let lineCount = 1, lineLen = 0;
  for (const w of words) {
    if (lineLen + w.length + 1 > charsPerLine && lineLen > 0) { lineCount++; lineLen = w.length; }
    else { lineLen += w.length + 1; }
  }

  const textHeight = lineCount * BASE.lineHeight;
  const cardHeight = BASE.padding + BASE.headerHeight + textHeight + BASE.footerHeight;

  const colors = theme === 'dark' 
    ? { bg: '#192734', border: '#38444d', name: '#fff', handle: '#8b98a5', text: '#fff' }
    : { bg: '#fff', border: '#e1e8ed', name: '#14171a', handle: '#657786', text: '#14171a' };

  // Word wrap for rendering
  const lines: string[] = [];
  let line = '', lw = 0;
  for (const w of words) {
    const ww = (w.length + 1) * avgCharWidth;
    if (lw + ww > textAreaWidth && lw > 0) { lines.push(line.trim()); line = w; lw = w.length * avgCharWidth; }
    else { line += (line ? ' ' : '') + w; lw += ww; }
  }
  if (line) lines.push(line.trim());

  const handle = tweetData.twitter_handle?.startsWith('@') ? tweetData.twitter_handle : `@${tweetData.twitter_handle}`;
  const textX = x + BASE.padding + BASE.avatarSize + BASE.contentGap;

  return (
    <g className="tweet-card" style={{ cursor: 'default' }} onClick={onClick}>
      <rect x={x} y={y} width={maxWidth} height={cardHeight} rx={BASE.cornerRadius} fill={colors.bg} stroke={colors.border} />
      <circle cx={x + BASE.padding + BASE.avatarSize/2} cy={y + BASE.padding + BASE.avatarSize/2} r={BASE.avatarSize/2} fill="#e1e8ed" />
      
      {tweetData.profile_image_url ? (
        <image x={x + BASE.padding} y={y + BASE.padding} width={BASE.avatarSize} height={BASE.avatarSize} href={tweetData.profile_image_url} />
      ) : (
        <text x={x + BASE.padding + BASE.avatarSize/2} y={y + BASE.padding + BASE.avatarSize/2 + 5} textAnchor="middle" fontSize="15" fontWeight="bold" fill="#657786">
          {(tweetData.expert_name || '?').charAt(0).toUpperCase()}
        </text>
      )}

      <text x={textX} y={y + BASE.padding + BASE.nameFontSize} fontSize={BASE.nameFontSize} fontWeight="bold" fill={colors.name}>
        {tweetData.expert_name}
      </text>
      <text x={textX} y={y + BASE.padding + BASE.nameFontSize + BASE.handleFontSize + 3} fontSize={BASE.handleFontSize} fill={colors.handle}>
        {handle}
      </text>

      {lines.map((txt, i) => (
        <text key={i} x={textX} y={y + BASE.padding + BASE.headerHeight + i * BASE.lineHeight} fontSize={BASE.textFontSize} fill={colors.text}>
          {txt}
        </text>
      ))}

      {tweetData.source_url && (
        <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); window.open(tweetData.source_url!, '_blank'); }}>
          <circle cx={x + maxWidth - BASE.padding - 10} cy={y + cardHeight - BASE.padding - 10} r={10} fill="#1a1a1a" stroke="#F7931A" strokeWidth={1.5} />
        </g>
      )}
    </g>
  );
};

export default TweetVisualization;
